import { PlusIcon } from '@heroicons/react/16/solid'
import { Box, Input, Pagination, Select } from 'View/Common/index.js'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import Actions from '../../../Flux/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { selectProjects, selectTotal } from '../../../Flux/Selector/index.js'
import { ProjectComponentCompact } from './ProjectComponentCompact.js'
import { useProperty } from '../../../Framework/View/index.js'
import { useInteraction } from '../../../Framework/View/Hooks/useInteraction.js'
import { debounceTime, skip } from 'rxjs'
import { takeOptions } from '../OrderManagementPage/OrderManagementPage.js'
import { useModal } from '../../Modal/UseModal.js'
import { ProjectSort, projectSortOptions, type ProjectSortValue } from '../../../Model/Project/ProjectSort.js'
import { useNavigate } from 'react-router-dom'

export const ProjectManagementPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const projects = useSelector(selectProjects)
  const total = useSelector(selectTotal)

  const search$ = useProperty<string>('')
  const take$ = useProperty<string>('10')
  const sort$ = useProperty<ProjectSortValue>(ProjectSort.NEWEST)
  const [page, setPage] = useState<number>(1)
  const onPagination$ = useInteraction<number>()
  const { openModal } = useModal()

  const fetchProjects = useCallback(
    (page: number) =>
      dispatch(
        Actions[projectSlice.name].getAllProjectsRequest({
          size: parseInt(take$.value),
          // The backend's Pageable index is zero-based; `page` here is the 1-based UI page.
          page: page - 1,
          sort: sort$.value,
          filters: [
            {
              value: search$.value,
              parameter: 'name',
              filterType: 'SUBSTR'
            }
          ]
        })
      ),
    [dispatch, take$, sort$, search$]
  )

  useEffect(() => {
    const onPagination$$ = onPagination$.subscribe((page) => {
      setPage(page)
    })

    return () => onPagination$$.unsubscribe()
  }, [onPagination$])

  /* Initial load, and whenever the page changes. */
  useEffect(() => {
    fetchProjects(page)
  }, [fetchProjects, page])

  /* Refetch when a control changes. skip(1) drops each BehaviorSubject's current value, which the
     effect above has already fetched — without it every control would refire on mount. */
  useEffect(() => {
    const subscriptions = [search$.pipe(debounceTime(400)), take$, sort$].map((source$) =>
      source$.pipe(skip(1)).subscribe(() => fetchProjects(page))
    )

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [fetchProjects, page, search$, take$, sort$])

  return (
    <Box title="Projects" width="full">
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="flex-1 min-w-[12rem]">
          <span className="block text-xs font-medium text-gray-500 mb-1">Search</span>
          <Input type="text" onChange$={search$} placeholder="Project name" />
        </div>
        <div className="flex-shrink-0">
          <span className="block text-xs font-medium text-gray-500 mb-1">Sort</span>
          <Select<ProjectSortValue> onChange$={sort$} options={projectSortOptions} initialValue={sort$.value} />
        </div>
        <div className="flex-shrink-0">
          <span className="block text-xs font-medium text-gray-500 mb-1">Per page</span>
          <Select onChange$={take$} options={takeOptions} initialValue={take$.value} />
        </div>
        <button
          data-testid="create-project"
          role="button"
          className="btn btn-sm btn-primary flex-shrink-0 h-[2.125rem]"
          onClick={() => openModal('createProject', {}, (projectId) => navigate(`/projects/${projectId}`))}
        >
          <PlusIcon className="w-4" />
          Create New Project...
        </button>
      </div>
      {projects === 'LOADING' ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner" />
        </div>
      ) : projects && projects.length > 0 ? (
        <div className="flex flex-col gap-3">
          {projects.map((p) => (
            <ProjectComponentCompact project={p} key={p.project.id} />
          ))}
        </div>
      ) : (
        <div className="text-center p-8 text-gray-500">No projects found. Create a new project to get started.</div>
      )}
      <Pagination onClick$={onPagination$} page={page} total={total} take={parseInt(take$.value)} />
    </Box>
  )
}
